import { useEffect } from 'react'
import { BubbleDataPoint, Chart, ChartConfiguration, ChartDataset, ChartEvent, ChartTypeRegistry, Point, registerables } from 'chart.js'
import { PowerData } from '../../types/charts'
import 'chartjs-adapter-luxon'
import { Series, SeriesOptions } from '../../utils/series'
import { maxOf, minOf } from '../../utils/math'
import zoomPlugin from 'chartjs-plugin-zoom'
import { useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { sprintf } from 'sprintf-js'
import { isDebug } from '../../utils/misc'

Chart.register(zoomPlugin, ...registerables)

export interface PowerChartProps {
  id: string
  data: PowerData[]
  solar?: PowerData[]
  options: SeriesOptions
  noLabels?: boolean
  vat?: number
  onClick?: () => void
}

export function PowerChart(props: PowerChartProps): React.ReactElement {
  const { id, data, noLabels, options, solar } = props

  const theme = useTheme()
  const { t } = useTranslation()

  useEffect(() => {
    const powerSeries = Series.simplify(data, options)
    const solarSeries = solar ? Series.simplify(solar, options) : undefined

    if (solarSeries) {
      powerSeries.result = powerSeries.result.map((r, idx) => r === null ? r : r + (solarSeries.result[idx] || 0))
    }

    const yMax1 = Math.ceil(maxOf(powerSeries.result))
    const yMax2 = solarSeries ? Math.ceil(maxOf(solarSeries.result)) : 0
    const lowest = Math.floor(minOf(powerSeries.result))
    const yMin = isDebug('high-accuracy') || lowest < 0 ? undefined : 0
    const yMax = Math.max(yMax1, yMax2)

    const datasets: ChartDataset<keyof ChartTypeRegistry, (number | [number, number] | Point | BubbleDataPoint | null)[]>[] = [
      {
        data: powerSeries.result,
        label: t('Consumption ( kW )'),
        showLine: true,
        cubicInterpolationMode: 'monotone',
        pointRadius: 0,
        borderWidth: 2,
        backgroundColor: theme.palette.secondary.main,
        borderColor: theme.palette.secondary.main,
      },
    ]

    if (solarSeries) {
      datasets.push({
        data: solarSeries.result,
        label: t('Consumption ( kW )'),
        showLine: true,
        cubicInterpolationMode: 'monotone',
        pointRadius: 0,
        borderWidth: 2,
        backgroundColor: theme.palette.primary.main,
        borderColor: theme.palette.primary.main,
      })
    }

    const config = {
      type: 'line',

      data: {
        labels: powerSeries.labels,
        datasets,
      },

      options: {
        animation: false,

        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
            position: 'bottom'
          },
        },

        scales: {
          x: {
            ticks: {
              color: theme.palette.grey[400],
              maxRotation: 0,
              font: {
                size: 10
              },
            },
            grid: {
              display: false
            },
            display: !noLabels
          },

          y: {
            min: yMin,
            max: yMax,
            grid: {
              color: '#dfdfdf',
              lineWidth: 0.075,
              borderDash: [5, 15],
            },
            ticks: {
              color: theme.palette.grey[400],
              minRotation: 0,
              callback: (value: number) => sprintf(`%.${isDebug('high-accuracy') ? 3 : 1}f`, value as number / 1000) + 'kW'
            },
            title: {
              display: false,
              text: t('kW')
            }
          }
        },

        onClick: (e: ChartEvent) => {
          props.onClick && props.onClick()
        },
      }
    }

    const currentChart = new Chart(
      document.getElementById(id) as HTMLCanvasElement,
      config as ChartConfiguration
    )

    return () => {
      currentChart.destroy()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, solar])

  return <canvas data-cy={`canvas-power-chart-${id}`} style={{ display: 'block', width: '100%' }} id={id}/>
}
